import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   MORTUARY MANAGEMENT MODULE — MLC / Death Registry
   - Death Registration (Natural / MLC / DAMA)
   - Body Receipt & Storage Tracking
   - Post-Mortem / PMCT Coordination
   - Body Release Authorization
   - Death Certificate Generation
   - MLC (Medico-Legal Case) Register
   - Burial / Cremation Orders
═══════════════════════════════════════════════════════════ */

const CAUSE_OF_DEATH = ['Natural Causes', 'Cardiac Arrest', 'Respiratory Failure', 'Septic Shock', 'Multi-Organ Failure', 'Trauma / RTA', 'Drowning', 'Burns', 'Poisoning / OD', 'Suicide', 'Unknown / Under Investigation'];
const BODY_STATUS = { 'In Storage': '#f59e0b', Released: '#10b981', 'Pending PM': '#6366f1', 'Under PM': '#f97316', Claimed: '#059669', 'Unclaimed': '#dc2626' };

function DeathRegisterTab() {
  const { records: deaths, create, patch, loading } = useHmsRecords('mortuary');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', age: '', gender: 'M', uhid: '', wardName: '', timeOfDeath: '', pronouncedByDoctor: '', causeOfDeath: CAUSE_OF_DEATH[0], isMLC: false, mlcNo: '', pmRequired: false, relativeContactName: '', relativePhone: '', relation: '' });

  const registerDeath = async () => {
    const bodyNo = `BODY-${String(deaths.length + 1).padStart(3, '0')}`;
    await create({
      ...form,
      age: Number(form.age) || 0,
      bodyNo,
      storageCell: `Tray-${deaths.length + 1}`,
      bodyStatus: form.pmRequired ? 'Pending PM' : 'In Storage',
      pmStatus: form.pmRequired ? 'Pending PM' : null,
      deathCertNo: null,
      releasedAt: null,
    });
    setShowForm(false);
    setForm({ name: '', age: '', gender: 'M', uhid: '', wardName: '', timeOfDeath: '', pronouncedByDoctor: '', causeOfDeath: CAUSE_OF_DEATH[0], isMLC: false, mlcNo: '', pmRequired: false, relativeContactName: '', relativePhone: '', relation: '' });
  };

  const releaseBody = async (d) => {
    await patch(d.recordId || d.id, {
      bodyStatus: 'Released',
      releasedAt: new Date().toISOString(),
      deathCertNo: d.deathCertNo || `DC-${new Date().getFullYear()}-${String(deaths.length).padStart(3, '0')}`,
    });
  };

  function printDeathCert(d) {
    const win = window.open('', '_blank', 'width=700,height=900');
    if (!win) return;
    win.document.write(`<html><head><title>Death Certificate</title><style>body{font-family:Arial,sans-serif;max-width:650px;margin:20px auto;font-size:13px}.header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:15px}.seal{text-align:center;margin:20px 0;font-size:40px}table{width:100%;border-collapse:collapse}td{padding:8px 0;border-bottom:1px dashed #e2e8f0}label{color:#64748b;font-size:11px;display:block;margin-bottom:2px}.footer{margin-top:40px;display:flex;justify-content:space-between}</style></head><body>
    <div class="header"><h2>DEATH CERTIFICATE</h2><p>Issued under Registration of Births and Deaths Act, 1969</p><p>Hospital Name: HCEN Networks · Certificate No: ${d.deathCertNo || 'DC-PENDING'}</p></div>
    <div class="seal">⚕</div>
    <table>
      <tr><td><label>Name of Deceased</label><strong>${d.name}</strong></td><td><label>Age / Sex</label><strong>${d.age} yrs / ${d.gender === 'M' ? 'Male' : 'Female'}</strong></td></tr>
      <tr><td><label>Date & Time of Death</label><strong>${new Date(d.timeOfDeath).toLocaleString('en-IN')}</strong></td><td><label>Place of Death</label><strong>${d.wardName}, HCEN Hospital</strong></td></tr>
      <tr><td colspan="2"><label>Cause of Death</label><strong>${d.causeOfDeath}</strong></td></tr>
      <tr><td><label>Pronounced by</label><strong>${d.pronouncedByDoctor}</strong></td><td><label>UHID</label><strong>${d.uhid || 'Unknown'}</strong></td></tr>
      ${d.isMLC ? `<tr><td colspan="2"><label>MLC Number</label><strong style="color:#dc2626">${d.mlcNo}</strong></td></tr>` : ''}
    </table>
    <div class="footer"><div style="text-align:center"><div>_________________________</div><div>Medical Officer / Registrar</div></div><div style="text-align:center"><div>_________________________</div><div>Hospital Seal</div></div></div>
    <p style="font-size:10px;text-align:center;color:#94a3b8;margin-top:20px">This is a computer-generated certificate. Issued at ${new Date().toLocaleString('en-IN')}</p>
    </body></html>`);
    win.document.close(); win.print();
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Deaths', val: deaths.length, color: '#64748b' }, { label: 'In Storage', val: deaths.filter(d => d.bodyStatus === 'In Storage').length, color: '#f59e0b' }, { label: 'MLC Cases', val: deaths.filter(d => d.isMLC).length, color: '#dc2626' }, { label: 'PM Pending', val: deaths.filter(d => d.pmStatus === 'Pending PM').length, color: '#6366f1' }, { label: 'Released', val: deaths.filter(d => d.bodyStatus === 'Released').length, color: '#10b981' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Register Death'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #64748b' }}>
          <h3 className="full" style={{ marginTop: 0 }}>☪ Death Registration</h3>
          <div className="field"><label>Patient Name *</label><input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="field"><label>UHID (if admitted)</label><input className="input" value={form.uhid} onChange={e => setForm(f => ({ ...f, uhid: e.target.value }))} /></div>
          <div className="field"><label>Age</label><input type="number" className="input" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} /></div>
          <div className="field"><label>Gender</label><select className="input" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option><option value="U">Unknown</option></select></div>
          <div className="field"><label>Ward / Location</label><input className="input" value={form.wardName} onChange={e => setForm(f => ({ ...f, wardName: e.target.value }))} /></div>
          <div className="field"><label>Time of Death *</label><input type="datetime-local" className="input" value={form.timeOfDeath} onChange={e => setForm(f => ({ ...f, timeOfDeath: e.target.value }))} /></div>
          <div className="field"><label>Pronounced by *</label><input className="input" value={form.pronouncedByDoctor} onChange={e => setForm(f => ({ ...f, pronouncedByDoctor: e.target.value }))} /></div>
          <div className="field"><label>Cause of Death</label><select className="input" value={form.causeOfDeath} onChange={e => setForm(f => ({ ...f, causeOfDeath: e.target.value }))}>{CAUSE_OF_DEATH.map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="field"><label>MLC Case?</label><select className="input" value={form.isMLC ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, isMLC: e.target.value === 'yes' }))}><option value="no">No</option><option value="yes">Yes — MLC</option></select></div>
          {form.isMLC && <div className="field"><label>MLC Number</label><input className="input" value={form.mlcNo} onChange={e => setForm(f => ({ ...f, mlcNo: e.target.value }))} /></div>}
          <div className="field"><label>Post-Mortem Required?</label><select className="input" value={form.pmRequired ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, pmRequired: e.target.value === 'yes' }))}><option value="no">No</option><option value="yes">Yes</option></select></div>
          <div className="field"><label>Relative Contact Name</label><input className="input" value={form.relativeContactName} onChange={e => setForm(f => ({ ...f, relativeContactName: e.target.value }))} /></div>
          <div className="field"><label>Relative Phone</label><input className="input" value={form.relativePhone} onChange={e => setForm(f => ({ ...f, relativePhone: e.target.value }))} /></div>
          <div className="field"><label>Relation</label><input className="input" value={form.relation} onChange={e => setForm(f => ({ ...f, relation: e.target.value }))} /></div>
          <div className="full form-actions">
            <button type="button" className="btn btn-primary" onClick={registerDeath}>Register Death</button>
          </div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Death Register</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Body No.</th><th>Name</th><th>Age/Sex</th><th>Time of Death</th><th>Cause</th><th>Ward</th><th>Pronounced By</th><th>MLC</th><th>PM</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {deaths.map(d => (
                <tr key={d.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6366f1' }}>{d.bodyNo}</td>
                  <td style={{ fontWeight: 700 }}>{d.name}<div style={{ fontSize: 10, color: '#94a3b8' }}>{d.uhid || 'No UHID'}</div></td>
                  <td>{d.age}y / {d.gender}</td>
                  <td style={{ fontSize: 11 }}>{new Date(d.timeOfDeath).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td style={{ fontSize: 11, maxWidth: 120 }}>{d.causeOfDeath}</td>
                  <td>{d.wardName}</td>
                  <td style={{ fontSize: 11 }}>{d.pronouncedByDoctor}</td>
                  <td>{d.isMLC ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>MLC {d.mlcNo}</span> : '—'}</td>
                  <td>{d.pmRequired ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: '#eff6ff', color: '#2563eb' }}>{d.pmStatus || 'Required'}</span> : '—'}</td>
                  <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: (BODY_STATUS[d.bodyStatus] || '#94a3b8') + '18', color: BODY_STATUS[d.bodyStatus] || '#94a3b8', border: `1px solid ${(BODY_STATUS[d.bodyStatus] || '#94a3b8')}40`, whiteSpace: 'nowrap' }}>{d.bodyStatus}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 10 }} onClick={() => printDeathCert(d)}>🖨 Cert</button>
                      {d.bodyStatus !== 'Released' && <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10 }} onClick={() => releaseBody(d)}>✓ Release</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MlcRegisterTab() {
  const { records: deaths, patch } = useHmsRecords('mortuary');
  const mlcCases = deaths.filter(d => d.isMLC);

  const [mlcState, setMlcState] = useState({});
  const [saving, setSaving] = useState({});

  const getMlc = (id) => mlcState[id] || {};
  const setMlcField = (id, field, value) =>
    setMlcState(prev => ({ ...prev, [id]: { ...getMlc(id), [field]: value } }));

  const saveMlc = async (c) => {
    setSaving(prev => ({ ...prev, [c.id]: true }));
    try {
      const fields = getMlc(c.id);
      await patch(c.id, {
        policeStation: fields.policeStation ?? c.policeStation ?? '',
        firNo:         fields.firNo         ?? c.firNo         ?? '',
        mlcReport:     true,
      });
    } finally {
      setSaving(prev => ({ ...prev, [c.id]: false }));
    }
  };

  function printMlcReport(c) {
    const fields = getMlc(c.id);
    const ps = fields.policeStation ?? c.policeStation ?? '—';
    const fir = fields.firNo ?? c.firNo ?? '—';
    const win = window.open('', '_blank', 'width=700,height=900');
    if (!win) return;
    win.document.write(`<html><head><title>MLC Report</title><style>
      body{font-family:Arial,sans-serif;max-width:660px;margin:20px auto;font-size:13px}
      .header{text-align:center;border-bottom:2px solid #dc2626;padding-bottom:10px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse}
      td{padding:8px 0;border-bottom:1px dashed #e2e8f0}
      label{color:#64748b;font-size:11px;display:block;margin-bottom:2px}
      .footer{margin-top:40px;display:flex;justify-content:space-between}
    </style></head><body>
    <div class="header">
      <h2 style="color:#dc2626">MEDICO-LEGAL CASE (MLC) REPORT</h2>
      <p>Hospital Name: HCEN Networks &nbsp;|&nbsp; MLC No: <strong>${c.mlcNo}</strong></p>
    </div>
    <table>
      <tr><td><label>Patient Name</label><strong>${c.name}</strong></td>
          <td><label>Age / Sex</label><strong>${c.age} yrs / ${c.gender === 'M' ? 'Male' : c.gender === 'F' ? 'Female' : c.gender}</strong></td></tr>
      <tr><td><label>Time of Death</label><strong>${new Date(c.timeOfDeath).toLocaleString('en-IN')}</strong></td>
          <td><label>Ward</label><strong>${c.wardName}</strong></td></tr>
      <tr><td colspan="2"><label>Nature / Cause</label><strong>${c.causeOfDeath}</strong></td></tr>
      <tr><td><label>Pronounced By</label><strong>${c.pronouncedByDoctor}</strong></td>
          <td><label>UHID</label><strong>${c.uhid || 'Unknown'}</strong></td></tr>
      <tr><td><label>Police Station</label><strong>${ps}</strong></td>
          <td><label>FIR No.</label><strong>${fir}</strong></td></tr>
      <tr><td><label>PM Status</label><strong>${c.pmStatus || 'Not Required'}</strong></td>
          <td><label>Body No.</label><strong>${c.bodyNo}</strong></td></tr>
    </table>
    <div class="footer">
      <div style="text-align:center"><div>_________________________</div><div>Medical Officer</div></div>
      <div style="text-align:center"><div>_________________________</div><div>Hospital Seal</div></div>
    </div>
    <p style="font-size:10px;text-align:center;color:#94a3b8;margin-top:20px">
      Generated at ${new Date().toLocaleString('en-IN')}
    </p>
    </body></html>`);
    win.document.close(); win.print();
  }

  return (
    <div>
      <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
        🚨 <strong>MLC (Medico-Legal Cases):</strong> All MLC cases must be reported to police immediately. Post-mortem is mandatory unless waived by magistrate. Preserve all evidence.
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0, color: '#dc2626' }}>MLC Register</h3>
        {mlcCases.length === 0 ? <p className="hint table-empty">No MLC cases registered today.</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>MLC No.</th><th>Name</th><th>Time of Admission/Death</th><th>Nature of Case</th><th>Police Station</th><th>FIR No.</th><th>PM Status</th><th>Action</th></tr></thead>
              <tbody>
                {mlcCases.map(c => {
                  const state = getMlc(c.id);
                  const ps  = state.policeStation !== undefined ? state.policeStation : (c.policeStation || '');
                  const fir = state.firNo         !== undefined ? state.firNo         : (c.firNo         || '');
                  return (
                    <tr key={c.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 900, color: '#dc2626' }}>{c.mlcNo}</td>
                      <td style={{ fontWeight: 700 }}>{c.name}</td>
                      <td>{new Date(c.timeOfDeath).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td>{c.causeOfDeath}</td>
                      <td>
                        <input
                          className="input"
                          style={{ width: 140, padding: '2px 8px', fontSize: 11 }}
                          placeholder="Police station..."
                          value={ps}
                          onChange={e => setMlcField(c.id, 'policeStation', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          style={{ width: 100, padding: '2px 8px', fontSize: 11 }}
                          placeholder="FIR No."
                          value={fir}
                          onChange={e => setMlcField(c.id, 'firNo', e.target.value)}
                        />
                      </td>
                      <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#eff6ff', color: '#2563eb' }}>{c.pmStatus || 'Not Required'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            style={{ fontSize: 10 }}
                            disabled={saving[c.id]}
                            onClick={() => saveMlc(c)}
                          >
                            {saving[c.id] ? '⏳' : '💾'} Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{ fontSize: 10 }}
                            onClick={() => printMlcReport(c)}
                          >
                            🖨 MLC Report
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MortuaryPage() {
  const [tab, setTab] = useState('register');
  return (
    <PageShell title="Mortuary Management" subtitle="Death Registry · Body Storage · MLC Register · Death Certificates · Post-Mortem Coordination">
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'register', label: '📋 Death Register' }, { id: 'mlc', label: '🚨 MLC Register' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'register' && <DeathRegisterTab />}
      {tab === 'mlc' && <MlcRegisterTab />}
    </PageShell>
  );
}
